import React, { useEffect, useRef } from "react";
import anime from "animejs";

/**
 * @component
 * @example
 * const items = ['Item 1', 'Item 2', 'Item 3'];
 * return <AutoplayCarousel items={items} titleText="Sample Carousel" duplicationFactor={2} tickerSpeed={240} />;
 *
 * @param {Array} props.items - an array of elements
 * @param {string} props.titleText - title text displayed at center
 * @param {number} [props.totalWidthPreload] - optional, but will ensure the animation is smooth as calculateTotalWidth may return inconsistent value
 * @param {number} [props.duplicationFactor=2] - # of times duplicated to ensure the looping animation
 * @param {number} [props.tickerSpeed=240] - the speed of the carousel animation in pixels per second.
 * @returns {React.ReactElement}
 */
const AutoplayCarousel = ({ items, titleText, totalWidthPreload, duplicationFactor = 2, tickerSpeed = 120 }) => {
  const displayedItems = Array.from({ length: duplicationFactor }, () => items).flat();
  const carouselRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const calculateTotalWidth = () => {
      if (!carouselRef.current?.children) {
        return 0;
      }
      return Array.from(carouselRef.current.children).reduce(
        (acc, child) => acc + child.offsetWidth, 0,
      );
    };

    const initializeAnimation = () => {
      if (!carouselRef.current) {
        return;
      }

      // defer the initialization due to images may not be fully loaded, causing calculateTotalWidth to return incorrect value
      requestAnimationFrame(() => {
        const totalWidth = totalWidthPreload || calculateTotalWidth();
        const initialOffset = totalWidth / duplicationFactor;
        animationRef.current = anime({
          targets: carouselRef.current,
          translateX: -initialOffset, // ensure loop by translating to starting position
          duration: (totalWidth / tickerSpeed) * 1000,
          easing: "linear",
          loop: true,
          autoplay: true,
        });
      });
    };

    // add a small delay to wait for images and style to load to begin animation
    setTimeout(() => {
      initializeAnimation();
    }, 500);

    return () => {
      if (animationRef.current) {
        animationRef.current.pause();
        animationRef.current = null;
      }
    };
  }, [items, duplicationFactor, tickerSpeed]);

  return (
    <div className="overflow-hidden">
      <div className="text-neutral-600 text-center font-bold">{titleText}</div>
      <div
        ref={carouselRef}
        className="flex transition-transform duration-0 shrink-0"
      >
        {displayedItems.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="px-4 py-2 shrink-0">
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AutoplayCarousel;
